/* ===================================================================
   THE HERO STOPS MOVING WHEN NOBODY IS LOOKING AT IT.

   The hero is the most expensive surface on this site to composite: four aurora
   curtains, the horizon under them, three bottles carrying a float and an orbit,
   and the button's turning ring. Measured at 1440x900, those are twelve elements
   on infinite animations covering 3.8 times the viewport in layers — every one of
   which the compositor keeps ticking, and keeps a texture for, whether the section
   is on screen or a page and a half above it.

   Nothing about that is wrong while the visitor is *at* the hero. It is entirely
   wasted from the moment they scroll past it, which on this page is immediately:
   the configurator is the next thing down and is where the visit actually happens.
   So this file watches the section and sets one attribute on it; the stylesheet
   does the rest, pausing every animation inside and releasing the `will-change`
   promotions so the browser can drop the layers it is holding.

   **Two sections answer to it and both write the same attribute.** The measurement
   above was taken on `sections/hero-soul-dog.liquid`; `sections/hero-scene.liquid`
   replaced it as the home page's hero and trades the carousel for a label loop and
   an arrow, which is fewer moving elements over the same aurora. One attribute name
   serves both deliberately — it is one behaviour, and a second name for it would be
   a second rule to keep in step for no gain.

   **What it deliberately is not.** It is not a scroll listener — an
   IntersectionObserver costs nothing per frame, which a scroll handler on the
   busiest surface of the page cannot claim. It is not a play/pause *control*; the
   visitor's own preference is `prefers-reduced-motion`, which the section already
   answers by rendering its finished still composition, and this file has no
   opinion about it either way. And it is not a fade or a stagger: the pause lands
   while the section is off screen, so nothing is ever seen starting or stopping.

   Progressive enhancement: with this file absent, blocked, or in a browser without
   `IntersectionObserver`, the attribute is never set and the hero animates exactly
   as it did before — which is the behaviour this replaces, so the failure mode is
   the old state rather than a broken one.
   =================================================================== */

(() => {
  if (window.__soulHeroMotionBooted) return;
  window.__soulHeroMotionBooted = true;

  /* **Two roots, and it is a selector list rather than a second file.** The home page's
     hero is `sections/hero-scene.liquid` now; `sections/hero-soul-dog.liquid` is still in
     `templates/index.json`, disabled, and one flag brings it back. A query naming only the
     old root made this whole file a no-op the moment the new section rendered — the
     attribute would never be set, and every animation on the page's most expensive surface
     would keep ticking while the visitor is a page and a half below it.

     Only one of the two ever renders at a time, so in practice this is a list of one. It
     is written as a list, and observed by iteration, because the failure mode of the
     alternative is silent: nothing throws, nothing logs, and the section simply costs what
     it used to. */
  const heroes = document.querySelectorAll('.soul-hero, .soul-scene');
  if (heroes.length === 0 || typeof IntersectionObserver !== 'function') return;

  /* A tenth of the viewport of margin, so the hero is already running by the time
     any of it is visible. Coming back up the page is the case that matters: a
     section that started moving *after* it appeared would read as a stutter, and
     the promotion it needs is a repaint that is better spent before it is on
     screen than during the frame it arrives. */
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        /* `entry.target`, never a captured element: one observer watches every root it was
           given, and reading the element off the entry is what keeps that correct if a
           template ever renders both. */
        entry.target.toggleAttribute('data-soul-hero-still', !entry.isIntersecting);
      }
    },
    { rootMargin: '10% 0px 10% 0px' }
  );

  for (const hero of heroes) {
    observer.observe(hero);
  }
})();
